/**
 * Brand-authored join page — types shared by the public renderer, the brand
 * editor and the admin toggle. Mirrors `reveshare-backend/src/custom-join-page`.
 */

export interface JoinPageHosts {
  image: string[];
  font: string[];
  style: string[];
}

export interface JoinPageToken {
  name: string;
  description: string;
  example: string;
}

export interface JoinPageSpec {
  version: number;
  limits: {
    htmlBytes: number;
    cssBytes: number;
    maxNodes: number;
    maxDepth: number;
    maxDataUriBytes: number;
    warnImages: number;
    warnKeyframes: number;
    warnInfiniteAnimations: number;
    warnBlurPx: number;
  };
  html: {
    allowedTags: string[];
    svgTags: string[];
    removedTags: string[];
    globalAttributes: string[];
    tagAttributes: Record<string, string[]>;
    svgAttributes: string[];
  };
  css: {
    rejectedAtRules: string[];
    rejectedFunctions: string[];
    rejectedProperties: string[];
    injectedVariables: string[];
  };
  hosts: JoinPageHosts;
  links: { policy: string; description: string };
  tokens: JoinPageToken[];
  bridge: {
    joinAttribute: string;
    stateAttribute: string;
    states: string[];
    labelAttributes: string[];
    conditionalAttribute: string;
  };
}

/** What the public brand-details payload carries when a custom page is live. */
export interface LiveCustomJoinPage {
  html: string;
  css: string;
  version: number;
  hosts: JoinPageHosts;
}

export type LintSeverity = "error" | "warning";

export interface LintIssue {
  severity: LintSeverity;
  code: string;
  message: string;
  source: "html" | "css";
  line?: number;
  column?: number;
}

export interface LintResponse {
  ok: boolean;
  errors: LintIssue[];
  warnings: LintIssue[];
  compiledHtml: string;
  compiledCss: string;
  previewHtml: string;
  stats: { nodes: number; depth: number; images: number; keyframes: number };
}

export interface JoinPageVersionSummary {
  version: number;
  createdAt: string;
  createdVia: "EDITOR" | "MCP";
  clientId: string | null;
}

export interface JoinPageVersion extends JoinPageVersionSummary {
  html: string;
  css: string;
}

export type CustomJoinPageStatus = "DRAFT" | "PUBLISHED";

/** Editor bootstrap: `GET custom-join-page/me`. */
export interface CustomJoinPageState {
  brandName: string;
  enabled: boolean;
  adminNote: string | null;
  status: CustomJoinPageStatus;
  isLive: boolean;
  draft: { html: string; css: string };
  publishedVersion: number;
  publishedAt: string | null;
  lintReport: { warnings: LintIssue[]; stats: LintResponse["stats"]; lintedAt: string } | null;
  versions: JoinPageVersionSummary[];
  publicUrl: string | null;
  specVersion: number;
  tokenValues: Record<string, string>;
  allowedLinkHosts: string[];
  /** The brand's saved theme colours, injected into the frame as --rs-*. */
  theme: { primary: string; secondary: string };
  hosts: JoinPageHosts;
  /** Public URL of the MCP connector, or null when not configured. */
  mcpUrl: string | null;
}

/** Consent page: `GET oauth/requests/:id`. */
export interface OAuthConsentRequest {
  id: string;
  client: { clientName: string; clientUri: string | null; logoUri: string | null };
  scopes: string[];
  expiresAt: string;
}

/** A connected AI client: `GET oauth/me/connections`. */
export interface OAuthConnection {
  id: string;
  clientId: string;
  clientName: string;
  clientUri: string | null;
  scope: string;
  connectedAt: string;
  lastUsedAt: string | null;
  expiresAt: string;
}

export interface PublishResponse {
  version: number;
  publishedAt: string;
  publicUrl: string | null;
  warnings: LintIssue[];
}

/** Shape of a 400 from publish: the issues that blocked it. */
export interface PublishBlockedBody {
  message: string;
  errors: LintIssue[];
  warnings?: LintIssue[];
}

/** Admin list row field. */
export interface AdminCustomJoinPage {
  enabled: boolean;
  status: CustomJoinPageStatus;
  publishedVersion: number;
  note: string | null;
  openReports: number;
}

export const JOIN_PAGE_REPORT_REASONS = [
  { value: "SCAM", label: "Looks like a scam" },
  { value: "IMPERSONATION", label: "Pretends to be someone else" },
  { value: "OFFENSIVE", label: "Offensive content" },
  { value: "BROKEN", label: "Page is broken" },
  { value: "OTHER", label: "Something else" },
] as const;

export type JoinPageReportReason = (typeof JOIN_PAGE_REPORT_REASONS)[number]["value"];

/** Join-flow state the parent pushes into the frame. */
export type JoinFrameState = "join" | "joined" | "pending" | "loading";
